import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/ds/components/dialog";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";

type TUnsavedChangesPromptProps = {
  open: boolean;
  isSaving?: boolean;
  onClose: () => void;
  onLeaveWithoutSaving: () => void;
  onSaveAndLeave: () => void;
};

// `UnsavedChangesDialog` on the design system, for parts rebuilt on the design system.
export function UnsavedChangesPrompt(props: TUnsavedChangesPromptProps) {
  const { open, isSaving = false, onClose, onLeaveWithoutSaving, onSaveAndLeave } = props;
  const { t } = useTranslation("common");

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSaving && onClose()}>
      <DialogContent
        closeLabel={t("close")}
        showCloseButton={false}
        onClick={(e) => e.stopPropagation()}
        className="w-[440px] rounded-xl p-5 shadow-[0px_51px_14px_0px_rgba(24,24,27,0),0px_33px_13px_0px_rgba(24,24,27,0),0px_18px_11px_0px_rgba(24,24,27,0.01),0px_8px_8px_0px_rgba(24,24,27,0.01),0px_2px_5px_0px_rgba(24,24,27,0.02)] sm:rounded-xl"
      >
        <DialogHeader className="space-y-1 text-start">
          <DialogTitle className="flex h-8 items-center">
            <span className="block typo-h6 text-innoveraZinc-900">Unsaved Changes</span>
          </DialogTitle>
          <DialogDescription>
            <span className="block typo-body-sm text-innoveraZinc-700">
              Your changes won&apos;t be applied until Relaunch Analysis is completed. Save your changes now and
              relaunch later.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="items-center sm:space-x-1">
          <Button type="button" variant="outline" disabled={isSaving} onClick={onLeaveWithoutSaving}>
            Leave Without Saving
          </Button>
          <Button type="button" disabled={isSaving} onClick={onSaveAndLeave}>
            {isSaving ? (
              <>
                <Spinner data-icon="inline-start" aria-label={t("loading")} />
                Saving…
              </>
            ) : (
              <>Save &amp; Leave</>
            )}
          </Button>
        </DialogFooter>
        <DialogClose className="absolute end-[13px] top-[13px] rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none">
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
